import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthCenteredLayout from '@layouts/AuthCenteredLayout/AuthCenteredLayout';
import Input from '@components/Form/Input/Input';
import Button from '@components/Button/Button';
import Icon from '@components/common/Icon';

interface ResetPasswordFormValues { newPassword: string; confirmPassword: string; }

function ResetPasswordPage() {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const { register, handleSubmit, watch, formState: { errors, isSubmitting } } =
        useForm<ResetPasswordFormValues>();

    const onSubmit = async (data: ResetPasswordFormValues) => {
        console.log('Update password', data);
        navigate('/admin/signin');
    };

    const EyeToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
        <button type="button" onClick={onToggle}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="flex items-center">
            <Icon name={show ? 'eyeHide' : 'eyeShow'} className='w-[12px] 2xl:w-[14px] h-auto' />
        </button>
    );

    return (
        <AuthCenteredLayout>
            <div className="bg-white rounded-xl shadow-[var(--shadow-lg)] border border-[var(--neutral-color)] p-[20px] 2xl:p-[30px] flex flex-col items-center">
                <div className="w-10 h-10 2xl:w-14 2xl:h-14 rounded-lg bg-[var(--primary-surface-color)] flex items-center justify-center mb-[20px] 2xl:mb-[30px]">
                    <Icon name="shieldCheck" className="w-auto h-[18px] 2xl:h-[26px]" />
                </div>
                <div className="text-center mb-[20px] 2xl:mb-[30px]">
                    <h1 className="text-[18px] 2xl:text-[24px]  font-semibold text-[var(--secondary-color)]">{t('reset_password.title')}</h1>
                    <p className="text-[12px] 2xl:text-[14px] text-[var(--disabled-text-color)]">{t('reset_password.subtitle')}</p>
                </div>
                <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col" noValidate>
                    <Input
                        label={t('reset_password.new_password_label')}
                        type={showNew ? 'text' : 'password'}
                        placeholder={t('reset_password.new_password_placeholder')} required
                        rightElement={<EyeToggle show={showNew} onToggle={() => setShowNew(v => !v)} />}
                        registration={register('newPassword', {
                            required: 'Password is required',
                            minLength: { value: 8, message: 'Minimum 8 characters' },
                        })}
                        error={errors.newPassword?.message}
                    />
                    <Input
                        label={t('reset_password.confirm_password_label')}
                        type={showConfirm ? 'text' : 'password'}
                        placeholder={t('reset_password.confirm_password_placeholder')} required
                        rightElement={<EyeToggle show={showConfirm} onToggle={() => setShowConfirm(v => !v)} />}
                        registration={register('confirmPassword', {
                            required: 'Please confirm your password',
                            validate: v => v === watch('newPassword') || 'Passwords do not match',
                        })}
                        error={errors.confirmPassword?.message}
                    />
                    <Button className='mt-[10px]' type="submit" variant="primary" fullWidth disabled={isSubmitting}>
                        {t('reset_password.update_button')}
                    </Button>
                </form>
            </div>
            <div className="flex justify-center mt-5">
                <Button variant="ghost" onClick={() => navigate('/admin/signin')}>
                    <Icon name="arrowLeft" size={14} className="mr-2" />
                    {t('reset_password.back_to_sign_in')}
                </Button>
            </div>
        </AuthCenteredLayout>
    );
}

export default ResetPasswordPage;
